import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { addContact } from "@/lib/api";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  sms: "",
  landline: "",
};

export default function AddContactDialog({ open, onOpenChange, userRecordId, onAdded }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!userRecordId) return;
    const hasSomething =
      form.firstName || form.lastName || form.email || form.sms || form.landline;
    if (!hasSomething) {
      toast.error("Add at least a name, email, or phone number");
      return;
    }
    setSaving(true);
    try {
      const res = await addContact({ userRecordId, ...form });
      toast.success(
        res.created_user ? "Contact added" : "Existing customer linked to your list"
      );
      setForm(EMPTY);
      onOpenChange(false);
      onAdded?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not add contact");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="add-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <UserPlus size={18} className="text-violet-600" />
            Add contact
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            We&apos;ll link this person to your contacts. No role required.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="First name"
              value={form.firstName}
              onChange={(v) => update("firstName", v)}
              testId="add-contact-first"
              autoFocus
            />
            <Field
              label="Last name"
              value={form.lastName}
              onChange={(v) => update("lastName", v)}
              testId="add-contact-last"
            />
          </div>
          <Field
            label="Email"
            type="email"
            placeholder="contact@example.com"
            value={form.email}
            onChange={(v) => update("email", v)}
            testId="add-contact-email"
          />
          <Field
            label="SMS / mobile phone"
            type="tel"
            placeholder="+1 555 123 4567"
            value={form.sms}
            onChange={(v) => update("sms", v)}
            testId="add-contact-sms"
          />
          <Field
            label="Landline / home phone"
            type="tel"
            placeholder="optional"
            value={form.landline}
            onChange={(v) => update("landline", v)}
            testId="add-contact-landline"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="add-contact-save"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Add contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId, autoFocus }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-zinc-600">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="h-11 rounded-xl"
        data-testid={testId}
      />
    </div>
  );
}
